import { useCallback, useEffect, useRef } from 'react';
import { identity } from 'lodash';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { APP_EVENT_TYPE } from '../appEventType';
import {
    transformChangeEvent,
    transformClickEvent,
    transformHoverEvent,
    transformIntersectionEvent,
} from '../utils/UIEventTransformers';
import { parseUIEvent } from '../utils/parseUIEvent';

const eventConfig = [
    { type: APP_EVENT_TYPE.CLICK, DOMEventType: 'click', transformer: transformClickEvent },
    { type: APP_EVENT_TYPE.CHANGE, DOMEventType: 'change', transformer: transformChangeEvent },
    { type: APP_EVENT_TYPE.HOVER, DOMEventType: 'mouseenter', transformer: transformHoverEvent },
];

export const useAppEventUICollector = push => {
    const isEnabled = useConfigMatch({ [FEATURE_FLAGS.CUSTOM_EVENT_AGGREAGATOR_ENABLED]: 'true' });
    const intersectionObserverRef = useRef(null);
    const mutationObserverRef = useRef(null);

    const listener = useCallback(
        (event, type, transformer = identity) => {
            const transformedEvent = transformer(parseUIEvent(event));
            if (transformedEvent) {
                transformedEvent.type = type;
                push(transformedEvent);
            }
        },
        [push]
    );

    const observeElement = element => {
        intersectionObserverRef.current?.observe(element);
    };

    const unobserveElement = element => {
        intersectionObserverRef.current?.unobserve(element);
    };

    const processElement = useCallback((element, processCallback) => {
        if (element.hasAttribute('data-visible')) {
            processCallback(element);
        }

        element.querySelectorAll('[data-visible]').forEach(processCallback);
    }, []);

    useEffect(
        function onDocumentLoad() {
            if (!isEnabled) {
                return;
            }

            intersectionObserverRef.current = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        listener(entry, APP_EVENT_TYPE.VISIBLE, transformIntersectionEvent);
                    }
                });
            });

            processElement(document.body, observeElement);

            return () => {
                if (intersectionObserverRef.current) {
                    intersectionObserverRef.current.disconnect();
                }
            };
        },
        [isEnabled, listener, processElement]
    );

    useEffect(
        function onDocumentChange() {
            if (!isEnabled) {
                return;
            }

            mutationObserverRef.current = new MutationObserver(mutations => {
                mutations.forEach(({ type, addedNodes, removedNodes, target, attributeName }) => {
                    if (type === 'childList') {
                        addedNodes.forEach(node => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                processElement(node, observeElement);
                            }
                        });
                        removedNodes.forEach(node => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                processElement(node, unobserveElement);
                            }
                        });
                    } else if (type === 'attributes') {
                        const attributeValue = target.getAttribute(attributeName);

                        if (attributeValue !== null) {
                            processElement(target, observeElement);
                        } else {
                            processElement(target, unobserveElement);
                        }
                    }
                });
            });

            mutationObserverRef.current.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['data-visible'],
            });

            return () => {
                if (mutationObserverRef.current) {
                    mutationObserverRef.current.disconnect();
                }
            };
        },
        [isEnabled, listener, processElement]
    );

    useEffect(() => {
        if (!isEnabled) {
            return;
        }
        const mappedConfig = eventConfig.map(({ DOMEventType, type, transformer }) => ({
            DOMEventType,
            listener: event => listener(event, type, transformer),
        }));

        mappedConfig.forEach(({ DOMEventType, listener }) => {
            document.body.addEventListener(DOMEventType, listener, {
                capture: true,
                passive: true,
            });
        });

        const cleaner = () => {
            mappedConfig.forEach(({ DOMEventType, listener }) => {
                document.body.removeEventListener(DOMEventType, listener, {
                    capture: true,
                    passive: true,
                });
            });
        };

        return cleaner;
    }, [listener, isEnabled]);
};
